<script lang="ts" setup>
import { onMounted, provide, ref, computed, watch, onUnmounted, onBeforeMount } from 'vue';
import { onBeforeRouteUpdate, RouteLocationNormalizedLoaded, useRouter } from 'vue-router';
import { SidebarMenu, SettingsBreadcrumbsModel, MenuBreadcrumbs } from '@/components';
import { AdapterService } from './adapter';
import { ProjectModel } from '@/models';
import { projectInjectKey } from '@/inject-keys';
import { GroupModel, QueueTasksModel } from '@/components/sidebar/models';
import { TirPmNotificationGroup, TirPmSkeletonLoader } from 'tir-components';
import { CurrentUser, UserRoleEnum } from 'tir-pm-user-core';
import CheckProjectAccessRequest from '@/models/CheckProjectAccessRequest';
import WeeekOptionsSwitchEnableRequest from '@/models/WeeekOptionsSwitchEnableRequest';
import MonitoringUserTasksPage from '@/pages/monitorings/monitoring-days/components/monitoring-user-tasks-page.vue';
import { CheckEstimateRequestModel } from './models';

const router = useRouter();

const adapter = new AdapterService();

/**Настройки хлебных крошек */
const breadcrumbsSettings = ref<SettingsBreadcrumbsModel>();
/**Видны ли хлебные крошки */
const visibleBreadcrumb = ref(false);

/**Получить настройки хлебных крошек */
const getBreadcrumbsSettingsByRoute = (route?: RouteLocationNormalizedLoaded) => {
  if (!route) return;

  return new SettingsBreadcrumbsModel({
    NamespaceId: route.params?.namespaceId?.toString(),
    GroupId: route.params?.groupId ? Number(route.params.groupId) : undefined,
    QueueId: route.params?.queueId?.toString(),
  });
};

/**Ссылка на компонент сайбара */
const $sidebar = ref<InstanceType<typeof SidebarMenu>>();
/**Информация о проекте */
const project = ref<ProjectModel>();
/**Есть ли доступ к задаче */
const isUserAccessTask = ref<boolean>(true);
/**Отсутствие информации об очереди */
const isNoQueueInformation = ref(false);
/**Был ли приглашен текущий юзер */
const checkInviteUser = ref<boolean>();

/**Является ли юзер овнером */
const isOwner = CurrentUser.Roles.includes(UserRoleEnum.Owner);
provide('project', project);
/**Админ проекта */
const isAdminProject = computed(() => !!project.value?.Admins?.some((a) => a.Id == CurrentUser.Id));
/**id проекта */
const projectId = computed(() => Number(router.currentRoute.value.params?.projectId));
/**Проверка на нахождение на странице мониторинга */
const hasMonitoring = computed(() => router.currentRoute.value.path.includes('/monitoring'));
/**id выбраного юзера в мониторинге */
const selectedUserIdMonitoring = computed(() => Number(router.currentRoute.value.query.userId));
/**id задачи */
const taskId = computed(() => Number(router.currentRoute.value.params?.taskId));
/**Находится ли юзер в деталке задачи */
const isTaskDetailPage = computed(() => router.currentRoute.value.name === 'task-detail');
/**Находится ли юзер на странице с заглушкой */
const isOnStubPage = computed(() => router.currentRoute.value.name === 'project-stub-page');

provide('isAdminProject', isAdminProject);
/**Приглашен ли текущий юзер */
provide('isInvited', checkInviteUser);

provide(
  projectInjectKey,
  computed(() => project.value),
);

/**Получить проект */
const getProject = async (projectId: number) => {
  const resp = await adapter.getProject(projectId);
  if (resp.IsSuccess) {
    project.value = resp.Value;
  }
};
/**Проврека доступа к проекту */
const checkProjectAccess = async () => {
  const resp = await adapter.checkProjectAccess(
    new CheckProjectAccessRequest({
      ProjectId: projectId.value,
    }),
  );
  if (!resp?.HasAccess && !isOwner) {
    router.push('/projects');
    return;
  }
};
/**Проверка, приглашён ли текущий пользователь */
const checkInviteToEstimate = async () => {
  const resp = await adapter.checkInviteToEstimate(
    new CheckEstimateRequestModel({
      TaskId: taskId.value,
    }),
  );
  if (resp.IsSuccess) {
    checkInviteUser.value = resp.Value.Invite;
  }
};
/**Установить заголово для вкладки браузера */
const setDocumentTitle = () => {
  if (router.currentRoute.value.path.endsWith('/namespaces')) {
    if (project.value?.Name) {
      const maxTitleLength = 18;
      const projectName =
        project.value.Name.length > maxTitleLength
          ? project.value.Name.substring(0, maxTitleLength) + '...'
          : project.value.Name;

      document.title = `Проект: ${projectName}`;
    }
    if (!projectId.value) document.title = 'Проекты';
  }
};

watch(
  [() => router.currentRoute.value, () => project.value?.Name],
  () => {
    setDocumentTitle();
  },
  { immediate: true },
);
/**Отслеживание изменения id задачи */
watch(
  () => taskId.value && isTaskDetailPage.value,
  () => {
    if (taskId.value) {
      checkInviteToEstimate();
    }
  },
  { immediate: true },
);

/**Проверка на доступ к задаче */
const checkIsUserAccessTask = (value) => {
  isUserAccessTask.value = value;
};

/**Отсутсвие данных очереди */
const onQueueEmpty = (value) => {
  isNoQueueInformation.value = value;
};
/**Отслуживание изменение id проекта */
watch(
  () => projectId.value,
  async () => {
    if (projectId.value) await checkProjectAccess();
  },
  { immediate: true },
);

/**После рендера компонента */
onMounted(async () => {
  if (isNaN(projectId.value)) return;
  await getProject(projectId.value);
  breadcrumbsSettings.value = getBreadcrumbsSettingsByRoute(router.currentRoute.value);
  visibleBreadcrumb.value = true;
});

onBeforeRouteUpdate(async (to) => {
  isUserAccessTask.value = true;
  breadcrumbsSettings.value = getBreadcrumbsSettingsByRoute(to);
  const projectId = Number(to.params?.projectId);
  if (isNaN(projectId)) return;
  if (project.value.Id !== projectId) {
    await getProject(projectId);
  }
});

/**
 * * Переключение настройки включения/выключения интеграции с Weeek
 */
const switchEnableWeeek = async () => {
  const response = await adapter.switchEnableAsync(
    new WeeekOptionsSwitchEnableRequest({
      ProjectId: projectId.value,
    }),
  );
  if (response.IsSuccess) {
    project.value.WeekEnable = response.Value.WeeekEnable;
  }
};

const userData = ref();

const checkRightsFormQueue = (data) => {
  userData.value = data;
};
</script>
<template>
  <div class="project-tasks-layout">
    <TirPmNotificationGroup group="general" position="top-right" :zIndex="20000" />
    <template v-if="!project">
      <div class="project-tasks-layout__skeleton-content">
        <TirPmSkeletonLoader width="14rem" height="5rem" borderRadius="0.5rem" />
        <TirPmSkeletonLoader width="42.375rem" height="2rem" borderRadius="0.5rem" />
      </div>
    </template>
    <template v-if="project">
      <SidebarMenu ref="$sidebar" />
      <div class="project-tasks-layout__content">
        <MenuBreadcrumbs
          v-if="!hasMonitoring && !isOnStubPage"
          :settings="breadcrumbsSettings"
          :project="project"
        />
        <div :class="{ 'project-tasks-layout__main': !selectedUserIdMonitoring }">
          <RouterView
            :project="project"
            :userData="userData"
            :isNoQueueInformation="isNoQueueInformation"
            :isAccessWeeek="project.WeekEnable"
            @updateAllowImportWeeek="switchEnableWeeek"
            @checkRightsFormQueue="checkRightsFormQueue"
            @uppdateProject="getProject(projectId)"
            @isNoQueueInformation="onQueueEmpty"
            @checkIsUserAccessTask="checkIsUserAccessTask"
          />
        </div>
      </div>
    </template>
  </div>
</template>
<style lang="scss" scoped>
@keyframes skeleton-loading {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

.project-tasks-layout {
  display: flex;

  &__access-denied {
    width: 100%;
    &:empty {
      display: none;
    }
  }
  &__sidebar-menu-skeleton {
    width: 14rem;
    background-color: var(--neutral-10, #eaeaeb);
  }
  &__menu-breadcrumbs-skeleton {
    width: 42.375rem;
    height: 2rem;
    border-radius: 0.5rem;
    background-color: var(--neutral-10, #eaeaeb);
  }
  &__skeleton-content {
    display: flex;
    align-items: center;
    gap: 2rem;
    padding: 1.375rem 2.25rem 1.25rem 1.5rem;
  }
  &__main-skeleton {
    height: 75rem;
    border-radius: 0.5rem 0.5rem 0rem 0rem;
    background-color: var(--neutral-10, #eaeaeb);
  }
  &__main-content-skeleton {
    display: grid;
    margin-top: 1.313rem;
    width: 100%;
    gap: 0.75rem;
    grid-template-columns: minmax(0, 1fr) 27.5rem;
  }
  &__main-right-skeleton {
    border-radius: 0.5rem 0.5rem 0rem 0rem;
    height: 75rem;
    background-color: var(--neutral-10, #eaeaeb);
  }

  &__content {
    overflow-x: hidden;
    padding: 1.375rem 2.25rem 1.25rem 1.5rem;
    flex: 1;
  }

  &__title {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-secondary);
    line-height: 1.75rem;
    margin: 0;
    margin-bottom: 1.25rem;
  }

  &__main {
    margin-top: 1.313rem;
  }
}
</style>
